import React, { useState, useRef, useEffect } from "react";

const PAGEABLE_KEYS = ["location", "skills", "education"];

const useDebouncedSearch = (onSearch, filterKey, delay = 400) => {
  const timerRef = useRef(null);
  const [localTerm, setLocalTerm] = useState("");

  const handleChange = (value) => {
    setLocalTerm(value);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSearch(filterKey, value), delay);
  };

  const handleClear = () => {
    setLocalTerm("");
    clearTimeout(timerRef.current);
    onSearch(filterKey, "");
  };

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { localTerm, handleChange, handleClear };
};

const FacetSearchInput = ({ filterKey, onSearch, placeholder }) => {
  const { localTerm, handleChange, handleClear } = useDebouncedSearch(onSearch, filterKey);
  return (
    <div className="facet-search-wrap">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#8A9199" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input
        type="text"
        className="facet-search-input"
        placeholder={placeholder || `Search ${filterKey}…`}
        value={localTerm}
        onChange={(e) => handleChange(e.target.value)}
      />
      {localTerm && (
        <button className="facet-search-clear" onClick={handleClear}>×</button>
      )}
    </div>
  );
};

// ── Dual-range slider ─────────────────────────────────────────────────────────
const DualRangeSlider = ({ boundsMin, boundsMax, lo, hi, onChange, unit }) => {
  const clamp = (v, a, b) => Math.min(b, Math.max(a, Number(v)));
  const span = boundsMax - boundsMin || 1;
  const fillLeft = `${((lo - boundsMin) / span) * 100}%`;
  const fillWidth = `${((hi - lo) / span) * 100}%`;

  return (
    <div className="dual-range-wrap">
      <div className="dual-range-track">
        <div className="dual-range-fill" style={{ left: fillLeft, width: fillWidth }} />
        <input
          type="range"
          className="dual-range-input"
          min={boundsMin}
          max={boundsMax}
          value={lo}
          onChange={(e) => {
            const v = clamp(e.target.value, boundsMin, hi);
            onChange(v, hi);
          }}
        />
        <input
          type="range"
          className="dual-range-input"
          min={boundsMin}
          max={boundsMax}
          value={hi}
          onChange={(e) => {
            const v = clamp(e.target.value, lo, boundsMax);
            onChange(lo, v);
          }}
        />
      </div>
      <div className="dual-range-inputs">
        <input
          type="number"
          className="score-num-input"
          min={boundsMin}
          max={boundsMax}
          value={lo}
          onChange={(e) => {
            const v = clamp(e.target.value, boundsMin, boundsMax);
            if (!isNaN(v)) onChange(Math.min(v, hi), hi);
          }}
        />
        <span className="range-dash">–</span>
        <input
          type="number"
          className="score-num-input"
          min={boundsMin}
          max={boundsMax}
          value={hi}
          onChange={(e) => {
            const v = clamp(e.target.value, boundsMin, boundsMax);
            if (!isNaN(v)) onChange(lo, Math.max(v, lo));
          }}
        />
        {unit && <span className="range-unit">{unit}</span>}
      </div>
    </div>
  );
};

const TextMultiQuestion = ({ q, qVal, onResponse }) => {
  const [inputText, setInputText] = useState("");
  const [optSearch, setOptSearch] = useState("");
  const values = qVal.values || [];
  const textTags = qVal.text_contains_values || [];
  const allOptions = q.options || [];
  const filteredOpts = optSearch
    ? allOptions.filter((o) => (o.label || "").toLowerCase().includes(optSearch.toLowerCase()))
    : allOptions;

  const addValue = (val) => {
    if (!values.includes(val)) onResponse(q.question_id, { values: [...values, val] });
  };
  const removeValue = (val) => onResponse(q.question_id, { values: values.filter((v) => v !== val) });
  const addTag = () => {
    const t = inputText.trim();
    if (!t || textTags.includes(t)) return;
    onResponse(q.question_id, { text_contains_values: [...textTags, t] });
    setInputText("");
  };
  const removeTag = (t) => onResponse(q.question_id, { text_contains_values: textTags.filter((v) => v !== t) });

  return (
    <div className="question-filter-block">
      <div className="question-filter-label">{q.label}</div>
      {allOptions.length > 0 && (
        <>
          <input
            className="question-answer-search"
            type="search"
            placeholder="Search answers…"
            value={optSearch}
            onChange={(e) => setOptSearch(e.target.value)}
          />
          <div className="question-answer-meta">
            {optSearch
              ? `${filteredOpts.length} of ${allOptions.length} answers match`
              : `${allOptions.length} answer${allOptions.length === 1 ? "" : "s"}`}
          </div>
          <div className="question-option-check-list">
            {filteredOpts.map((opt) => (
              <label key={opt.value} className="question-option-check-row">
                <input type="checkbox" checked={values.includes(opt.value)} onChange={(e) => e.target.checked ? addValue(opt.value) : removeValue(opt.value)} />
                <span className="q-opt-label">{opt.label}</span>
                {opt.count != null && <span className="q-opt-count">{opt.count}</span>}
              </label>
            ))}
            {filteredOpts.length === 0 && <span className="filter-empty-msg">No matches</span>}
          </div>
        </>
      )}
      <div className="question-text-add-wrap">
        <input className="question-text-add-input" type="text" placeholder="Contains text…" value={inputText} onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} />
        <button type="button" className="question-text-add-btn" onClick={addTag}>+</button>
      </div>
      {textTags.length > 0 && (
        <div className="question-text-tag-list">
          {textTags.map((t) => (
            <span key={t} className="question-text-tag-item">{t}<button type="button" className="q-tag-remove" onClick={() => removeTag(t)}>×</button></span>
          ))}
        </div>
      )}
    </div>
  );
};

const CandidateFilterBody = ({
  filterKey, data, filterValues,
  onCheckbox, onPill, onRange, onStageScore, onResponse,
  onSearch, onLoadMore, loadingMore,
  skillsMatchMode, onSkillsModeChange,
}) => {
  if (!data) return <div className="filter-acc-loading">Loading…</div>;

  const ct = data.control_type;
  const options = data.options || [];
  const cfg = data.config || {};
  const isPageable = PAGEABLE_KEYS.includes(filterKey);
  const hasMore = cfg?.has_more;

  if (ct === "checkbox_list") {
    return (
      <div className="filter-option-list">
        {isPageable && onSearch && (
          <FacetSearchInput filterKey={filterKey} onSearch={onSearch} />
        )}
        {options.length === 0 && <span className="filter-empty-msg">No options available</span>}
        {options.map((opt) => {
          const val = String(opt.value ?? opt.id ?? "");
          const checked = (filterValues[filterKey] || []).includes(val);
          const hiringColor = filterKey === "hiring_status" ? (opt.color || null) : null;
          return (
            <label key={val} className="filter-check-row">
              <input type="checkbox" checked={checked} onChange={(e) => onCheckbox(filterKey, val, e.target.checked)} />
              {hiringColor ? (
                <span className="filter-opt-label hiring-status-badge" style={{ color: hiringColor, background: `${hiringColor}18` }}>
                  {opt.label}
                </span>
              ) : (
                <span className="filter-opt-label">{opt.label}</span>
              )}
              <span className="filter-opt-count">{opt.count ?? 0}</span>
            </label>
          );
        })}
        {isPageable && hasMore && (
          <button
            className="facet-load-more-btn"
            onClick={() => onLoadMore && onLoadMore(filterKey)}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <><div className="spinner-border" style={{ width: "0.75rem", height: "0.75rem", color: "#F75891", display: "inline-block", marginRight: 4 }} />Loading…</>
            ) : "Show more"}
          </button>
        )}
      </div>
    );
  }

  if (ct === "pill_list") {
    return (
      <div>
        {isPageable && onSearch && (
          <FacetSearchInput filterKey={filterKey} onSearch={onSearch} />
        )}
        <div className="filter-pill-grid">
          {options.map((opt) => {
            const val = String(opt.value ?? opt.id ?? "");
            const active = (filterValues[filterKey] || []).includes(val);
            return (
              <button key={val} type="button" className={`filter-pill ${active ? "active" : ""}`}
                onClick={() => onPill(filterKey, val)}>
                {!active && <span className="pill-plus">+ </span>}{opt.label}
              </button>
            );
          })}
          {options.length === 0 && <span className="filter-empty-msg">No options available</span>}
        </div>
        {isPageable && hasMore && (
          <button
            className="facet-load-more-btn"
            onClick={() => onLoadMore && onLoadMore(filterKey)}
            disabled={loadingMore}
            style={{ marginTop: 6 }}
          >
            {loadingMore ? (
              <><div className="spinner-border" style={{ width: "0.75rem", height: "0.75rem", color: "#F75891", display: "inline-block", marginRight: 4 }} />Loading…</>
            ) : "Show more"}
          </button>
        )}
      </div>
    );
  }

  if (ct === "range" && filterKey === "experience") {
    const lo = Number(cfg.min ?? 0);
    const hi = Number(cfg.max ?? 15);
    const fv = filterValues[filterKey] || { min: lo, max: hi };
    const unit = cfg.max_label || "yrs";
    return (
      <DualRangeSlider
        boundsMin={lo}
        boundsMax={hi}
        lo={fv.min ?? lo}
        hi={fv.max ?? hi}
        onChange={(newLo, newHi) => onRange(filterKey, newLo, newHi)}
        unit={unit}
      />
    );
  }

  if (ct === "range" && filterKey === "resume_score") {
    const rs = filterValues.resume_score || { min: 0, max: 100 };
    return (
      <DualRangeSlider
        boundsMin={0}
        boundsMax={100}
        lo={rs.min ?? 0}
        hi={rs.max ?? 100}
        onChange={(newLo, newHi) => onRange("resume_score", newLo, newHi)}
      />
    );
  }

  if (ct === "range") {
    const lo = Number(cfg.min ?? 0);
    const hi = Number(cfg.max ?? 15);
    const fv = filterValues[filterKey] || { min: lo, max: hi };
    return (
      <DualRangeSlider
        boundsMin={lo}
        boundsMax={hi}
        lo={fv.min ?? lo}
        hi={fv.max ?? hi}
        onChange={(newLo, newHi) => onRange(filterKey, newLo, newHi)}
        unit={cfg.max_label}
      />
    );
  }

  if (ct === "stage_range_list") {
    if (options.length === 0) return <span className="filter-empty-msg">No stages configured</span>;
    return (
      <div className="stage-filter-list">
        {options.map((stage) => {
          const stageId = String(stage.id ?? "");
          const sv = (filterValues.stage_scores || {})[stageId] || { min_percent: 0, max_percent: 100, status_ids: [] };
          const statusOpts = stage.meta?.status_options || [];
          return (
            <div key={stageId} className="stage-filter-item">
              <div className="stage-filter-head">
                <span>{stage.label} <span className="stage-total-cnt">({stage.count ?? 0})</span></span>
                <span className="stage-threshold-val">{sv.min_percent}–{sv.max_percent ?? 100}%</span>
              </div>
              <DualRangeSlider
                boundsMin={0}
                boundsMax={100}
                lo={sv.min_percent ?? 0}
                hi={sv.max_percent ?? 100}
                onChange={(newLo, newHi) => {
                  onStageScore(stageId, "min_percent", newLo);
                  onStageScore(stageId, "max_percent", newHi);
                }}
              />
            </div>
          );
        })}
      </div>
    );
  }

  if (ct === "question_controls") {
    const questions = cfg.questions || [];
    if (questions.length === 0) return <span className="filter-empty-msg">No questions</span>;
    return (
      <div className="question-filter-list">
        {questions.map((q) => {
          const qVal = (filterValues.candidate_responses || {})[q.question_id] || {};

          if (q.control_type === "segmented") {
            return (
              <div key={q.question_id} className="question-filter-block">
                <div className="question-filter-label">{q.label}</div>
                <div className="segmented-ctrl">
                  {(q.options || []).map((opt) => (
                    <button key={opt.value} type="button"
                      className={`seg-btn ${qVal.value === opt.value ? "active" : ""}`}
                      onClick={() => onResponse(q.question_id, { value: qVal.value === opt.value ? null : opt.value, text_contains: null })}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          }

          if (q.control_type === "select" || q.control_type === "text_select") {
            return (
              <div key={q.question_id} className="question-filter-block">
                <div className="question-filter-label">{q.label}</div>
                <select className="question-filter-select" value={qVal.value || "any"}
                  onChange={(e) => onResponse(q.question_id, { value: e.target.value === "any" ? null : e.target.value, text_contains: null })}>
                  <option value="any">Any</option>
                  {(q.options || []).filter((o) => o.value && o.value !== "any").map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}{opt.count != null ? ` (${opt.count})` : ""}</option>
                  ))}
                </select>
                {q.control_type === "text_select" && (
                  <input type="text" className="question-filter-text" placeholder="Or contains text…"
                    value={qVal.text_contains || ""}
                    onChange={(e) => onResponse(q.question_id, { text_contains: e.target.value || null, value: null })} />
                )}
              </div>
            );
          }

          if (q.control_type === "text") {
            return (
              <div key={q.question_id} className="question-filter-block">
                <div className="question-filter-label">{q.label}</div>
                <input type="text" className="question-filter-text" placeholder="Contains text…"
                  value={qVal.text_contains || ""}
                  onChange={(e) => onResponse(q.question_id, { text_contains: e.target.value || null })} />
              </div>
            );
          }

          if (q.control_type === "checkbox_list") {
            const vals = qVal.values || [];
            return (
              <div key={q.question_id} className="question-filter-block">
                <div className="question-filter-label">{q.label}</div>
                <div className="question-option-check-list">
                  {(q.options || []).map((opt) => (
                    <label key={opt.value} className="question-option-check-row">
                      <input type="checkbox" checked={vals.includes(opt.value)}
                        onChange={(e) => onResponse(q.question_id, { values: e.target.checked ? [...vals, opt.value] : vals.filter((v) => v !== opt.value) })} />
                      <span className="q-opt-label">{opt.label}</span>
                      {opt.count != null && <span className="q-opt-count">{opt.count}</span>}
                    </label>
                  ))}
                </div>
              </div>
            );
          }

          if (q.control_type === "text_multi") {
            return <TextMultiQuestion key={q.question_id} q={q} qVal={qVal} onResponse={onResponse} />;
          }

          if (q.control_type === "numeric_range") {
            const qcfg = q.config || {};
            return (
              <div key={q.question_id} className="question-filter-block">
                <div className="question-filter-label">{q.label}</div>
                <div className="score-range-inputs">
                  <input type="number" className="score-num-input" min={qcfg.min ?? 0} max={qcfg.max ?? 100}
                    value={qVal.numeric_min ?? qcfg.min ?? 0}
                    onChange={(e) => onResponse(q.question_id, { numeric_min: Number(e.target.value) })} />
                  <span className="range-dash">–</span>
                  <input type="number" className="score-num-input" min={qcfg.min ?? 0} max={qcfg.max ?? 100}
                    value={qVal.numeric_max ?? qcfg.max ?? 100}
                    onChange={(e) => onResponse(q.question_id, { numeric_max: Number(e.target.value) })} />
                </div>
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  }

  return null;
};

export default CandidateFilterBody;
